<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import { EDGE_TYPES } from '../useAgentFlowGraph';

const props = defineProps({
  node: { type: Object, default: null },
  edge: { type: Object, default: null },
  tools: { type: Array, default: () => [] },
  brains: { type: Array, default: () => [] },
});
const emit = defineEmits([
  'update:node',
  'update:edge',
  'delete-node',
  'delete-edge',
]);

const { t } = useI18n();

const setData = (key, value) => {
  emit('update:node', {
    ...props.node,
    data: { ...props.node.data, [key]: value },
  });
};
const setEdge = (key, value) => {
  emit('update:edge', { ...props.edge, [key]: value });
};

const toolOptions = computed(() => props.tools.map(tool => tool.name));
</script>

<template>
  <aside
    class="flex w-72 flex-shrink-0 flex-col gap-3 border-l border-n-weak p-3 overflow-y-auto"
  >
    <template v-if="node">
      <h3 class="text-sm font-semibold text-n-slate-12">
        {{ t(`AI_AGENTS.FLOW_EDITOR.NODE.${node.type.toUpperCase()}`) }}
      </h3>

      <template v-if="node.type === 'override_agent'">
        <label class="flex flex-col gap-1 text-xs font-medium text-n-slate-12">
          {{ t('AI_AGENTS.FLOW_EDITOR.FIELD.LABEL') }}
          <input
            data-testid="oa-label"
            :value="node.data.label"
            class="px-2 py-1.5 text-sm rounded-lg outline outline-1 outline-n-weak bg-n-solid-1 text-n-slate-12"
            @input="setData('label', $event.target.value)"
          />
        </label>
        <label class="flex flex-col gap-1 text-xs font-medium text-n-slate-12">
          {{ t('AI_AGENTS.FLOW_EDITOR.FIELD.ADDITIONAL_PROMPT') }}
          <textarea
            data-testid="oa-prompt"
            :value="node.data.additional_prompt"
            rows="4"
            class="px-2 py-1.5 text-sm rounded-lg outline outline-1 outline-n-weak bg-n-solid-1 text-n-slate-12"
            @input="setData('additional_prompt', $event.target.value)"
          />
        </label>
        <label class="flex flex-col gap-1 text-xs font-medium text-n-slate-12">
          {{ t('AI_AGENTS.FLOW_EDITOR.FIELD.TOOL_IDS') }}
          <select
            data-testid="oa-tools"
            multiple
            :value="node.data.tool_ids"
            class="px-2 py-1.5 text-sm rounded-lg outline outline-1 outline-n-weak bg-n-solid-1 text-n-slate-12"
            @change="
              setData(
                'tool_ids',
                Array.from($event.target.selectedOptions, o => o.value)
              )
            "
          >
            <option v-for="name in toolOptions" :key="name" :value="name">
              {{ name }}
            </option>
          </select>
        </label>
      </template>

      <template v-else-if="node.type === 'dispatch_tool'">
        <label class="flex flex-col gap-1 text-xs font-medium text-n-slate-12">
          {{ t('AI_AGENTS.FLOW_EDITOR.FIELD.TOOL_ID') }}
          <select
            data-testid="dt-tool"
            :value="node.data.tool_id"
            class="px-2 py-1.5 text-sm rounded-lg outline outline-1 outline-n-weak bg-n-solid-1 text-n-slate-12"
            @change="setData('tool_id', $event.target.value)"
          >
            <option value="">—</option>
            <option v-for="name in toolOptions" :key="name" :value="name">
              {{ name }}
            </option>
          </select>
        </label>
      </template>

      <template v-else-if="node.type === 'agent_transfer'">
        <label class="flex flex-col gap-1 text-xs font-medium text-n-slate-12">
          {{ t('AI_AGENTS.FLOW_EDITOR.FIELD.TARGET_BRAIN') }}
          <select
            data-testid="at-target"
            :value="node.data.target_brain_id"
            class="px-2 py-1.5 text-sm rounded-lg outline outline-1 outline-n-weak bg-n-solid-1 text-n-slate-12"
            @change="
              setData('target_brain_id', Number($event.target.value) || null)
            "
          >
            <option :value="null">—</option>
            <option v-for="b in brains" :key="b.id" :value="b.id">
              {{ b.display_name }}
            </option>
          </select>
        </label>
        <label class="flex flex-col gap-1 text-xs font-medium text-n-slate-12">
          {{ t('AI_AGENTS.FLOW_EDITOR.FIELD.CONDITION') }}
          <input
            :value="node.data.condition"
            class="px-2 py-1.5 text-sm rounded-lg outline outline-1 outline-n-weak bg-n-solid-1 text-n-slate-12"
            @input="setData('condition', $event.target.value)"
          />
        </label>
      </template>

      <template v-else-if="node.type === 'transfer_to_number'">
        <label class="flex flex-col gap-1 text-xs font-medium text-n-slate-12">
          {{ t('AI_AGENTS.FLOW_EDITOR.FIELD.PHONE') }}
          <input
            data-testid="tn-phone"
            :value="node.data.phone"
            class="px-2 py-1.5 text-sm rounded-lg outline outline-1 outline-n-weak bg-n-solid-1 text-n-slate-12"
            @input="setData('phone', $event.target.value)"
          />
        </label>
      </template>

      <p v-else class="text-xs text-n-slate-11">
        {{ t('AI_AGENTS.FLOW_EDITOR.NO_CONFIG') }}
      </p>

      <Button
        data-testid="delete-node"
        color="ruby"
        variant="outline"
        size="sm"
        :label="t('AI_AGENTS.FLOW_EDITOR.DELETE_NODE')"
        @click="emit('delete-node', node.id)"
      />
    </template>

    <template v-else-if="edge">
      <h3 class="text-sm font-semibold text-n-slate-12">
        {{ t('AI_AGENTS.FLOW_EDITOR.EDGE.TITLE') }}
      </h3>
      <label class="flex flex-col gap-1 text-xs font-medium text-n-slate-12">
        {{ t('AI_AGENTS.FLOW_EDITOR.EDGE.TYPE') }}
        <select
          data-testid="edge-type"
          :value="edge.type"
          class="px-2 py-1.5 text-sm rounded-lg outline outline-1 outline-n-weak bg-n-solid-1 text-n-slate-12"
          @change="setEdge('type', $event.target.value)"
        >
          <option v-for="type in EDGE_TYPES" :key="type" :value="type">
            {{ t(`AI_AGENTS.FLOW_EDITOR.EDGE.${type.toUpperCase()}`) }}
          </option>
        </select>
      </label>
      <label
        v-if="edge.type === 'llm'"
        class="flex flex-col gap-1 text-xs font-medium text-n-slate-12"
      >
        {{ t('AI_AGENTS.FLOW_EDITOR.EDGE.CONDITION') }}
        <input
          data-testid="edge-condition"
          :value="edge.condition"
          class="px-2 py-1.5 text-sm rounded-lg outline outline-1 outline-n-weak bg-n-solid-1 text-n-slate-12"
          @input="setEdge('condition', $event.target.value)"
        />
      </label>
      <label
        v-if="edge.type === 'expression'"
        class="flex flex-col gap-1 text-xs font-medium text-n-slate-12"
      >
        {{ t('AI_AGENTS.FLOW_EDITOR.EDGE.EXPR') }}
        <input
          data-testid="edge-expr"
          :value="edge.expr"
          class="px-2 py-1.5 text-sm rounded-lg outline outline-1 outline-n-weak bg-n-solid-1 text-n-slate-12"
          @input="setEdge('expr', $event.target.value)"
        />
      </label>
      <Button
        data-testid="delete-edge"
        color="ruby"
        variant="outline"
        size="sm"
        :label="t('AI_AGENTS.FLOW_EDITOR.DELETE_EDGE')"
        @click="emit('delete-edge', edge.id)"
      />
    </template>
  </aside>
</template>
